import { Drawer, Box, Stack, Typography, IconButton, Button, Divider } from '@mui/material'
import { XMarkIcon, BellSlashIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { useMarkAllNotificationsViewed, useMarkNotificationViewed } from '../../../apiLayer/queryHooks/mutations'
import { InAppNotificationType } from 'pure-js/enums/InAppNotificationType'
import type {
  InAppNotification,
  ReportReadyPayload,
  FeedbackReadyPayload,
  GameReminderPayload
} from 'pure-js/types/InAppNotification'
import { isGameReminderDisabled } from '../../utils/gameReminderVisibility'
import { OrgRole } from 'pure-js/types/OrganizationMembership'
import { FacilitatorRoutes, GamerRoutes } from '../../../config/routes/routeEnums'
import { useCanAccessGenerateReports } from '../../hooks/useCanAccessGenerateReports'
import { NotificationHistoryItem } from './NotificationHistoryItem'
import { useTranslation } from '../../hooks/useIntractionTranslate'
import { useVisibleInAppNotifications } from '../../hooks/useVisibleInAppNotifications'
import { INTERAX_COLORS } from '../../../constants/colors'
import { FACILITATOR_COLORS } from '../../../constants/colors'

interface NotificationSidebarProps {
  open: boolean
  onClose: () => void
  orgRole?: OrgRole
}

export const NotificationSidebar = ({ open, onClose, orgRole }: NotificationSidebarProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { canAccessGenerateReports } = useCanAccessGenerateReports()
  const notifications = useVisibleInAppNotifications()
  const { mutate: markViewed } = useMarkNotificationViewed()
  const { mutate: markAllViewed } = useMarkAllNotificationsViewed()

  const hasUnviewed = notifications.some((n) => !n.viewedAt)
  const unviewedCount = notifications.filter((n) => !n.viewedAt).length

  const handleNavigate = (notification: InAppNotification) => {
    if (notification.type === InAppNotificationType.GAME_REMINDER) {
      const payload = notification.payload as GameReminderPayload
      if (isGameReminderDisabled(payload, Date.now())) return
    }
    if (!notification.viewedAt) {
      markViewed(notification.id)
    }

    if (notification.type === InAppNotificationType.REPORT_READY) {
      const payload = notification.payload as ReportReadyPayload
      if (canAccessGenerateReports) {
        navigate(FacilitatorRoutes.GENERATE_REPORT, { state: { reportId: payload.reportId } })
      } else {
        navigate(FacilitatorRoutes.UPCOMING_ACTIVITIES)
      }
    } else if (notification.type === InAppNotificationType.FEEDBACK_READY) {
      const payload = notification.payload as FeedbackReadyPayload
      navigate(GamerRoutes.LATEST_SESSION_FEEDBACK, { state: { sessionId: payload.sessionId } })
    } else if (notification.type === InAppNotificationType.GAME_REMINDER) {
      if (orgRole === OrgRole.FACILITATOR) {
        navigate(FacilitatorRoutes.UPCOMING_ACTIVITIES)
      } else {
        navigate(GamerRoutes.UPCOMING_ACTIVITIES)
      }
    }

    onClose()
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 360, md: 360 },
          background: FACILITATOR_COLORS.facilitatorSidebarBackground,
          borderLeft: `1px solid rgba(255,255,255,0.08)`,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* ── Header ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: `1px solid rgba(255,255,255,0.08)`,
          flexShrink: 0
        }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography variant="h6" fontWeight={700} sx={{ color: INTERAX_COLORS.white, letterSpacing: '-0.01em' }}>
            {t('notifications.sidebar.title')}
          </Typography>
          {unviewedCount > 0 && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: '10px',
                background: INTERAX_COLORS.sun,
                minWidth: 22,
                textAlign: 'center'
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: INTERAX_COLORS.offBlack, fontSize: '11px', lineHeight: 1 }}
              >
                {unviewedCount}
              </Typography>
            </Box>
          )}
        </Stack>
        <IconButton
          onClick={onClose}
          aria-label={t('notifications.sidebar.closeAriaLabel')}
          size="small"
          sx={{
            color: INTERAX_COLORS.thunderGrey,
            '&:hover': { color: INTERAX_COLORS.white, background: 'rgba(255,255,255,0.08)' }
          }}
        >
          <XMarkIcon width={20} height={20} />
        </IconButton>
      </Stack>

      {/* ── Mark all as read ── */}
      {hasUnviewed && (
        <Box sx={{ px: 2.5, py: 1, borderBottom: `1px solid rgba(255,255,255,0.06)`, flexShrink: 0 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => markAllViewed()}
            sx={{
              color: INTERAX_COLORS.sun,
              textTransform: 'none',
              p: 0,
              fontSize: '13px',
              fontWeight: 500,
              '&:hover': { color: INTERAX_COLORS.white, background: 'transparent' }
            }}
          >
            {t('notifications.sidebar.markAllRead')}
          </Button>
        </Box>
      )}

      {/* ── List ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {notifications.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" gap={1.5} sx={{ height: '100%', px: 3, py: 8 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <BellSlashIcon width={24} height={24} style={{ color: INTERAX_COLORS.thunderGrey }} />
            </Box>
            <Typography variant="body2" sx={{ color: INTERAX_COLORS.thunderGrey, textAlign: 'center' }}>
              {t('notifications.sidebar.emptyState')}
            </Typography>
          </Stack>
        ) : (
          <Stack gap={0.25}>
            {notifications.map((notification, idx) => {
              const isDisabled =
                notification.type === InAppNotificationType.GAME_REMINDER &&
                isGameReminderDisabled(notification.payload as GameReminderPayload, Date.now())
              return (
                <Box key={notification.id}>
                  <NotificationHistoryItem
                    notification={notification}
                    onNavigate={handleNavigate}
                    isDisabled={isDisabled}
                  />
                  {idx < notifications.length - 1 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.04)', mx: 2 }} />}
                </Box>
              )
            })}
          </Stack>
        )}
      </Box>
    </Drawer>
  )
}
